import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Slide,
    AppBar,
    Toolbar,
    Link,
    Avatar,
    TextField,
    InputAdornment,
    Button,
} from '@mui/material';
// Material UI Icons
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import SearchIcon from '@mui/icons-material/Search';

// React Icons (for platforms not covered by MUI)
import { FaSpotify as SpotifyIcon } from 'react-icons/fa';
import { FaSoundcloud as SoundcloudIcon } from 'react-icons/fa';
import { FaTiktok as TikTokIcon } from 'react-icons/fa';
import { FaBandcamp as BandcampIcon } from 'react-icons/fa';

import { useAuth } from '../auth/AuthContext';
import { Stack } from '@mui/system';
import { useParams, useNavigate } from 'react-router-dom';

// Define a transition for the Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function to get the correct icon for social links
const getSocialIcon = (type) => {
    switch (type) {
        case 'website': return <LanguageIcon sx={{ mr: 0.5 }} />;
        case 'facebook': return <FacebookIcon sx={{ mr: 0.5 }} />;
        case 'instagram': return <InstagramIcon sx={{ mr: 0.5 }} />;
        case 'twitter': return <TwitterIcon sx={{ mr: 0.5 }} />;
        case 'youtube': return <YouTubeIcon sx={{ mr: 0.5 }} />;
        case 'spotify': return <SpotifyIcon sx={{ mr: 0.5 }} />;
        case 'bandcamp': return <BandcampIcon sx={{ mr: 0.5 }} />;
        case 'soundcloud': return <SoundcloudIcon sx={{ mr: 0.5 }} />;
        case 'tiktok': return <TikTokIcon sx={{ mr: 0.5 }} />;
        default: return <PersonOutlineIcon sx={{ mr: 0.5 }} />;
    }
};

const USERS_PER_PAGE = 8; // Número de usuarios a cargar por página

const UsersFeedScreen = () => {
    const { currentUser, dbUser, isLoading: authLoading } = useAuth();
    const { email: emailParam } = useParams();
    const navigate = useNavigate();

    const [allUsers, setAllUsers] = useState([]); // Almacena todos los usuarios fetcheados
    const [pageNumber, setPageNumber] = useState(1); // Página actual para la paginación del servidor
    const [hasMore, setHasMore] = useState(true); // Indica si hay más usuarios para cargar
    const [loadingInitialData, setLoadingInitialData] = useState(true); // Tracks initial data load for the main feed
    const [selectedUser, setSelectedUser] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [searchTerm, setSearchTerm] = useState(''); // Estado para el término de búsqueda
    const [loadingMore, setLoadingMore] = useState(false); // Estado para la carga adicional
    const [loadingUserForDialog, setLoadingUserForDialog] = useState(false); // New state for loading a specific user for the dialog

    // Effect for fetching users with pagination and search
    useEffect(() => {
        const fetchUsers = async () => {
            if (!authLoading && dbUser && dbUser.getPublicUsers) {
                setLoadingMore(pageNumber > 1);
                setLoadingInitialData(pageNumber === 1);

                try {
                    const fetchedData = await dbUser.getPublicUsers({
                        pageNum: pageNumber,
                        searchTerm: searchTerm,
                        limit: USERS_PER_PAGE
                    });
                    // Filter out current user if they are among the public users
                    fetchedData.users = fetchedData.users.filter(user => currentUser ? user.uid !== currentUser.uid : true);

                    if (pageNumber === 1) {
                        setAllUsers(fetchedData.users);
                    } else {
                        setAllUsers(prevUsers => [...prevUsers, ...fetchedData.users]);
                    }
                    setHasMore(fetchedData.users.length === USERS_PER_PAGE && (pageNumber * USERS_PER_PAGE) < fetchedData.totalCount);

                } catch (error) {
                    console.error('UsersFeedScreen - Failed to fetch public users:', error);
                    setAllUsers([]);
                    setHasMore(false);
                } finally {
                    setLoadingInitialData(false);
                    setLoadingMore(false);
                }
            } else if (!authLoading && !dbUser) {
                // If dbUser is not available (e.g., not logged in or auth is still loading)
                setAllUsers([]);
                setHasMore(false);
                setLoadingInitialData(false);
                setLoadingMore(false);
            }
        };

        const handler = setTimeout(() => {
            fetchUsers();
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [authLoading, dbUser, currentUser, pageNumber, searchTerm]);

    // Effect for handling the emailParam to open the detailed view
    // This effect will try to fetch the specific user if not already in allUsers
    useEffect(() => {
        const handleEmailParam = async () => {
            if (!authLoading && dbUser && emailParam) {
                const decodedEmailParam = decodeURIComponent(emailParam);
                let userToOpen = allUsers.find(
                    (user) => user.email.toLowerCase() === decodedEmailParam.toLowerCase()
                );

                if (!userToOpen) {
                    // If user is not in the current 'allUsers' (e.g., first load or not in current page),
                    // try to fetch them specifically.
                    setLoadingUserForDialog(true);
                    try {
                        // Assuming you have a method to get a single user by email
                        // You might need to add this method to your dbUser object if it doesn't exist
                        if (dbUser.getUserByEmail) {
                            userToOpen = await dbUser.getUserByEmail(decodedEmailParam);
                        } else {
                            console.warn('dbUser.getUserByEmail is not available. Cannot fetch specific user for dialog.');
                        }
                    } catch (error) {
                        console.error('Failed to fetch user by email for dialog:', error);
                    } finally {
                        setLoadingUserForDialog(false);
                    }
                }

                if (userToOpen && userToOpen.isProfilePublic) { // Ensure profile is public before opening
                    setSelectedUser(userToOpen);
                    setOpenDialog(true);
                } else if (emailParam && !openDialog) {
                    // If emailParam exists but user not found or not public, navigate away
                    navigate('/misbolos/users', { replace: true });
                }
            } else if (!emailParam && openDialog) {
                // If emailParam is removed from URL and dialog is open, close it
                setSelectedUser(null);
                setOpenDialog(false);
            }
        };

        handleEmailParam();
    }, [emailParam, allUsers, authLoading, dbUser, navigate, openDialog]); // Added dbUser to dependencies

    const handleCardClick = (user) => {
        const encodedEmail = encodeURIComponent(user.email);
        navigate(`/misbolos/users/${encodedEmail}`);
        // The useEffect above will handle setting selectedUser and openDialog based on emailParam
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        navigate('/misbolos/users');
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPageNumber(1); // Reset page number when search term changes
        setAllUsers([]); // Clear current users to show loading state for new search
        setHasMore(true); // Assume there are more results for a new search
    };

    const handleLoadMore = () => {
        setPageNumber(prevPage => prevPage + 1);
    };

    // if (authLoading || loadingInitialData || loadingUserForDialog) {
    //     return (
    //         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
    //             <CircularProgress />
    //             <Typography variant="h6" sx={{ marginLeft: 2 }}>
    //                 {loadingUserForDialog ? 'Cargando perfil de usuario...' : 'Cargando usuarios...'}
    //             </Typography>
    //         </Box>
    //     );
    // }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                padding: 4,
            }}
        >
            <Container maxWidth="lg">
                <Typography variant="h3" fontWeight="bold" align="center" gutterBottom>
                    Conoce Músicos
                </Typography>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    Descubre artistas individuales cerca de ti...
                </Typography>

                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Buscar músicos por nombre o biografía..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ maxWidth: 600, bgcolor: 'background.paper', borderRadius: 1 }}
                    />
                </Box>
                
                {/* Render if  authLoading || loadingInitialData || loadingUserForDialog*/}
                {authLoading || loadingInitialData || loadingUserForDialog ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                        <CircularProgress />
                        <Typography variant="h6" sx={{ marginLeft: 2 }}>
                            {loadingUserForDialog ? 'Cargando perfil de usuario...' : 'Cargando usuarios...'}
                        </Typography>
                    </Box>) : null
                }



                {allUsers.length === 0 && searchTerm === '' ? (
                    <Box sx={{ textAlign: 'center', mt: 8 }}>
                        <Typography variant="h5" color="text.secondary">
                            No se encontraron usuarios públicos por el momento. ¡Vuelve pronto!
                        </Typography>
                        {!dbUser && (
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                                Por favor, inicia sesión para ver usuarios públicos.
                            </Typography>
                        )}
                    </Box>
                ) : allUsers.length === 0 && searchTerm !== '' ? (
                    <Box sx={{ textAlign: 'center', mt: 8 }}>
                        <Typography variant="h5" color="text.secondary">
                            No se encontraron resultados para "{searchTerm}".
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                            Intenta con otra palabra clave.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={4}>
                        {allUsers.map((user) => (
                            <Grid item key={user._id} xs={12} sm={6} md={4} lg={3}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxShadow: 3,
                                        borderRadius: 2,
                                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: 6,
                                            cursor: 'pointer',
                                        },
                                    }}
                                    onClick={() => handleCardClick(user)}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Avatar
                                            src={`https://bluejims.com:5005/misbolos/image/${user.profilePicture}/q/1` || `https://via.placeholder.com/150/CCCCCC/808080?text=${user.displayName ? user.displayName.charAt(0) : user.email.charAt(0)}`}
                                            alt={user.displayName || user.email}
                                            sx={{ width: 120, height: 120, border: '3px solid #3f51b5' }}
                                        />
                                    </Box>
                                    <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                                        <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                                            {user.displayName || user.email.split('@')[0]}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontStyle: 'italic' }}>
                                            {user.bio || 'Sin biografía.'}
                                        </Typography>
                                        {user.openToGroupOffers !== undefined && (
                                            <Chip
                                                label={user.openToGroupOffers ? 'Abierto a ofertas de grupo' : 'No abierto a ofertas de grupo'}
                                                size="small"
                                                color={user.openToGroupOffers ? "success" : "default"}
                                                icon={user.openToGroupOffers ? <CheckCircleOutlineIcon /> : <NotInterestedIcon />}
                                                sx={{ mt: 1, fontWeight: 'bold' }}
                                            />
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Botón de Cargar Más */}
                {hasMore && allUsers.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Button
                            variant="contained"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            sx={{
                                bgcolor: '#3f51b5',
                                '&:hover': { bgcolor: '#303f9f' },
                                fontWeight: 'bold',
                                p: '10px 20px'
                            }}
                        >
                            {loadingMore ? <CircularProgress size={24} color="inherit" /> : 'Cargar más'}
                        </Button>
                    </Box>
                )}

            </Container>

            <Dialog
                fullScreen
                open={openDialog}
                onClose={handleCloseDialog}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative', background: 'linear-gradient(to right,rgb(31, 31, 31),rgb(20, 20, 20))' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleCloseDialog}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Detalles del Usuario
                        </Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent dividers sx={{ p: 0, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                    {selectedUser && (
                        <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                <Avatar
                                    src={`https://bluejims.com:5005/misbolos/image/${selectedUser.profilePicture}` || `https://via.placeholder.com/150/CCCCCC/808080?text=${selectedUser.displayName ? selectedUser.displayName.charAt(0) : selectedUser.email.charAt(0)}`}
                                    alt={selectedUser.displayName || selectedUser.email}
                                    sx={{ width: 180, height: 180, border: '4px solid #3f51b5' }}
                                />
                            </Box>

                            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#3f51b5', textAlign: 'center' }}>
                                {selectedUser.displayName || selectedUser.email.split('@')[0]}
                            </Typography>
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Typography variant="body1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                    <Box component="span" sx={{ ml: 1, fontWeight: 'medium' }}>{selectedUser.email}</Box>
                                </Typography>
                                {selectedUser.openToGroupOffers !== undefined && (
                                    <Chip
                                        label={selectedUser.openToGroupOffers ? 'Abierto a ofertas de grupo' : 'No abierto a ofertas de grupo'}
                                        color={selectedUser.openToGroupOffers ? "success" : "default"}
                                        icon={selectedUser.openToGroupOffers ? <CheckCircleOutlineIcon /> : <NotInterestedIcon />}
                                        sx={{ fontSize: '1rem', padding: '5px 10px', height: 'auto', fontWeight: 'bold' }}
                                    />
                                )}
                            </Box>

                            <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5', textAlign: 'center' }}>
                                Biografía:
                            </Typography>
                            <Typography variant="body1" paragraph sx={{ textAlign: 'center' }}>
                                {selectedUser.bio || 'Este usuario aún no ha escrito una biografía.'}
                            </Typography>
                            {/* Bandas del usuario */}
                            {selectedUser.misbolos?.groups && selectedUser.misbolos.groups.length > 0 && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5', textAlign: 'center' }}>
                                        Grupos:
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
                                    >
                                        {selectedUser.misbolos.groups.map((group, index) => {
                                            const imageUrl = `https://bluejims.com:5005/misbolos/image/${group.images?.[0]}/q/5`;

                                            return (
                                                // <Chip
                                                //     key={index}
                                                //     label={group.name}
                                                //     clickable
                                                //     color="primary"
                                                //     avatar={
                                                //         <Avatar
                                                //             src={imageUrl}
                                                //             alt={group.name}
                                                //             sx={{
                                                //                 border: '2px solid white', // white border around avatar
                                                //                 boxSizing: 'border-box',
                                                //             }}
                                                //         />
                                                //     }
                                                //     onClick={() => navigate(`/misbolos/groups/${encodeURIComponent(group.name).replace(/ /g, '-')}`)}
                                                // />
                                                <Link
                                                    key={group.name + index}
                                                    href={group.name}
                                                    // target="_blank"
                                                    rel="noopener"
                                                    color="inherit"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        textDecoration: 'none',
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        margin: '5px !important',
                                                        backgroundColor: '#e3f2fd',
                                                        '&:hover': {
                                                            backgroundColor: '#bbdefb',
                                                        },
                                                        gap: 1,
                                                    }}
                                                >
                                                    {group.images?.[0] && (
                                                        <Avatar
                                                            src={imageUrl}
                                                            alt={group.name}
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                border: '2px solid white',
                                                                boxSizing: 'border-box',
                                                            }}
                                                        />
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 'bold' }}
                                                        onClick={() => {
                                                            // e.stopPropagation();
                                                            navigate(
                                                                `/misbolos/groups/${encodeURIComponent(group.name).replace(/ /g, '-')}`
                                                            );
                                                        }}
                                                    >
                                                        {group.name}
                                                    </Typography>
                                                </Link>
                                            );
                                        })}
                                    </Stack>
                                </>
                            )}

                            {/* Enlaces del usuario */}
                            {selectedUser.socialLinks && selectedUser.socialLinks.length > 0 && (
                                <>
                                    <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#3f51b5', textAlign: 'center' }}>
                                        Enlaces Sociales:
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                                        {selectedUser.socialLinks.map((link, index) => (
                                            <Link
                                                key={index}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener"
                                                color="inherit"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    textDecoration: 'none',
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#e3f2fd',
                                                    '&:hover': {
                                                        backgroundColor: '#bbdefb',
                                                    },
                                                }}
                                            >
                                                {getSocialIcon(link.type)}
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {link.type.charAt(0).toUpperCase() + link.type.slice(1)}
                                                </Typography>
                                            </Link>
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog >
        </Box >
    );
};

export default UsersFeedScreen;